import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useAppSession } from "@/features/auth/session";
import { accountAccessOptions } from "@/features/auth/access";
import { analyticsPreferenceOptions } from "./api";
import { analytics, analyticsEnabled, browserPrivacyOptOut } from "./service";
import { useAnalyticsStopped } from "./preference";
import { analyticsScreen } from "./screens";
/** Observes resolved router templates only; public/token/admin screens are excluded. */
export function AnalyticsObserver() {
  const visibilityGeneration = useRef(0);
  const [visible, setVisible] = useState(
    typeof document !== "undefined" && document.visibilityState === "visible",
  );
  const session = useAppSession({ refetchOnMount: false });
  const actorKey = session.data?.user.id ?? "";
  const route = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId ?? "",
  });
  const visitKey = useRouterState({
    select: (state) => state.location.pathname,
  });
  const screen = analyticsScreen(route);
  const stopped = useAnalyticsStopped(actorKey);
  const account = useQuery({
    ...accountAccessOptions(actorKey),
    enabled: analyticsEnabled && !!actorKey,
    refetchOnMount: false,
  });
  const permitted =
    !!actorKey &&
    account.data?.id === actorKey &&
    !account.error &&
    !account.data.isBanned &&
    !account.data.isLocked &&
    !account.data.isDeleting &&
    !account.data.isPendingDelete;
  const preference = useQuery({
    ...analyticsPreferenceOptions(actorKey),
    enabled: analyticsEnabled && permitted && !!screen && visible,
    refetchOnMount: false,
  });
  const { refetch: refreshPreference } = preference;
  useEffect(() => {
    const onVisibility = () => {
      const generation = ++visibilityGeneration.current;
      analytics.clear();
      setVisible(false);
      if (document.visibilityState !== "visible") return;
      if (!analyticsEnabled || !permitted || !screen) {
        setVisible(true);
        return;
      }
      // Refresh cross-device consent before allowing a new foreground visit.
      void refreshPreference().finally(() => {
        if (
          generation === visibilityGeneration.current &&
          document.visibilityState === "visible"
        )
          setVisible(true);
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [permitted, screen, refreshPreference]);
  useEffect(() => {
    analytics.setContext({
      actorKey: actorKey || null,
      enabled:
        analyticsEnabled &&
        visible &&
        permitted &&
        !session.error &&
        !preference.error &&
        !!preference.data?.enabled &&
        !browserPrivacyOptOut() &&
        !!screen &&
        !stopped,
      consented: preference.data?.consented === true,
    });
    analytics.screen(screen, visitKey);
  }, [
    actorKey,
    permitted,
    preference.data,
    preference.error,
    screen,
    session.error,
    stopped,
    visitKey,
    visible,
  ]);
  useEffect(
    () => () => {
      visibilityGeneration.current++;
      analytics.clear();
    },
    [],
  );
  return null;
}
